import {
    OWN_QUESTION_COST,
    PREBUILT_COST,
    SPECIALIST_COST,
} from "./questionsQuotaApi";

/**
 * The token ladder sold on /questions and /account/questions.
 *
 * MUST stay in sync with QUESTIONS_PACKAGES in
 * site_server/routers_fastapi/payments.py — the server resolves count/days/amount
 * from the SKU it receives, so a mismatch here silently credits the wrong number
 * of tokens. The `tok-` prefix is required: bare numeric SKUs ("30", "360") are
 * the retired answer-count generation and still resolve server-side.
 *
 * Prices mirror the shape of the RU specialist ladder (torsunov.life
 * /ru/account/ask-specialist): a flat entry price, then a widening discount that
 * tops out around half the base rate.
 */
export type TokenPackSku =
    | "tok-10"
    | "tok-20"
    | "tok-35"
    | "tok-60"
    | "tok-120";

export interface TokenPack {
    sku: TokenPackSku;
    /** Tokens credited. */
    count: number;
    /** Validity of the credited package, in days. */
    days: number;
    /** Price in USD cents — display only; Stripe is charged server-side. */
    priceCents: number;
    /** Highlighted card. */
    featured?: boolean;
}

// The two top tiers carry more tokens than the ladder's linear rate implies. That
// is deliberate and it is why they are 60/120 rather than 50/100: the product ask
// was "30 own questions and 18 specialist questions at the $49.90 tier, 55 and 40
// at $74.90", and no flat cost table produces those. 50 tokens at 2/own is 25, not
// 30; and no token count yields both 30 own (needs 60-61) and 18 specialist (needs
// 54-56). Raising the token count instead of bending the per-action costs keeps
// every number on the card checkable by the buyer, and lands at or above the ask:
// 60 → 30 own / 20 specialist, 120 → 60 own / 40 specialist.
export const TOKEN_PACKS: TokenPack[] = [
    { sku: "tok-10", count: 10, days: 365, priceCents: 1500 },
    { sku: "tok-20", count: 20, days: 365, priceCents: 2700 },
    { sku: "tok-35", count: 35, days: 365, priceCents: 3990 },
    { sku: "tok-60", count: 60, days: 365, priceCents: 4990, featured: true },
    { sku: "tok-120", count: 120, days: 365, priceCents: 7490 },
];

export const getTokenPack = (sku: string): TokenPack | undefined =>
    TOKEN_PACKS.find((p) => p.sku === sku);

/** "$15" for whole dollars, "$39.90" otherwise — the ladder deliberately mixes both. */
export const formatPackPrice = (cents: number): string =>
    cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;

/** Discount vs the entry tier's per-token rate, as a whole percent (0 for the base). */
export const packDiscountPercent = (pack: TokenPack): number => {
    const base = TOKEN_PACKS[0];
    const baseRate = base.priceCents / base.count;
    const rate = pack.priceCents / pack.count;
    return Math.round((1 - rate / baseRate) * 100);
};

/** How many of each action a pack buys. Integer division — leftover tokens stay
 *  spendable on the cheaper actions, which is why the card shows all three. */
export const packYield = (pack: TokenPack) => ({
    answers: Math.floor(pack.count / PREBUILT_COST),
    ownQuestions: Math.floor(pack.count / OWN_QUESTION_COST),
    specialist: Math.floor(pack.count / SPECIALIST_COST),
});
